import { createInstance } from 'i18next';

import type { CustomLabels } from 'src/context/portalConfigContext';
import translation from 'src/i18n/translations/en-US.json';
import {
  getCustomizedLabels,
  INDIVIDUAL_TERMS,
} from 'src/utils/getCustomizedLabels';

export type ClientSettingsPage = 'authentication' | 'notifications';

export const getClientSettingsTerm = (customLabels?: CustomLabels) =>
  getCustomizedLabels({ customLabels: customLabels ?? null, isAssembly: true })
    .client_one;

export const getClientSettingsSlug = (term: string): string => {
  const slug = term
    .normalize('NFC')
    .toLowerCase()
    .replace(new RegExp('[^\\p{L}\\p{N}\\p{M}]+', 'gu'), '-')
    .replace(/^-+|-+$/g, '');
  return new RegExp('[\\p{L}\\p{N}]', 'u').test(slug) ? slug : 'client';
};

export const getClientSettingsPath = (term: string, page: ClientSettingsPage) =>
  `/settings/${encodeURIComponent(getClientSettingsSlug(term))}-${page}`;

const WORKSPACE_PREFIX_PATTERN = /^\/w\/[^/]+(?=\/)/;

// History decodes Unicode pathnames in the browser; HTTP requests keep them encoded.
// The workspace prefix is stripped because the canonical /w/<ws> URL must match the unprefixed path.
export const isClientSettingsPath = (
  pathname: string,
  term: string,
  page: ClientSettingsPage,
) => {
  const path = getClientSettingsPath(term, page);
  const unprefixed = pathname.replace(WORKSPACE_PREFIX_PATTERN, '');
  return unprefixed === path || unprefixed === decodeURI(path);
};

export const isClientSettingsTerm = (term: string, currentTerm: string) =>
  term === getClientSettingsSlug(currentTerm) ||
  INDIVIDUAL_TERMS.some((builtInTerm) => term === builtInTerm.toLowerCase());

// Route titles resolve before React initializes workspace translations; keep them request-independent.
const titleTranslations = createInstance();
void titleTranslations.init({
  lng: 'en',
  initImmediate: false,
  resources: {
    en: {
      translation: { settings_page_titles: translation.settings_page_titles },
    },
  },
  interpolation: { escapeValue: false },
});

export const getClientSettingsTitle = (
  term: string,
  page: ClientSettingsPage,
) => titleTranslations.t(($) => $.settings_page_titles[page], { term });

export const getClientSettingsTitleForPath = (
  pathname: string,
  term: string,
) => {
  const page = (['authentication', 'notifications'] as const).find(
    (candidate) => isClientSettingsPath(pathname, term, candidate),
  );
  return page ? getClientSettingsTitle(term, page) : undefined;
};
